// src/encounter/lib/chart-readout.ts
//
// Chart readout (switch `chartReadout`; Antonin's catalog ChartReadout, scrips-signal-ds feat/signal @166c3c35). PURE: no React,
// no I/O. In a note line, "\" typed at the end of the text lists the patient's chart data; a pick inserts a READ-ONLY readout.
//
// THE DATA IS THE LINE'S OWN. The sources are resolved from the `chartReads` the line already holds (to-chart-snapshot.ts:
// today's vitals, and the chart Labs tab's reads for the catalogue lab sets). Nothing is fetched here. A source is offered only
// when its read came back with values (`ok`): a pending or failed read is never offered as data.
//
// THE READOUT IS A CHART PIECE (note-pieces.ts) marked `readout`, kept at the END of the line's segments, each after a line
// break. So it is saved in the same `notes` Delta as any chart piece (Flutter reads its text), and the line shows it as a card
// under the text instead of inside it. `splitReadouts` and `joinReadouts` are the one place that layout is read and written.
import { LAB_SETS } from './lab-set-catalogue';
import type { LineSeg } from './note-line-delta';
import { lineDisplay } from './line-piece-text';
import { chartName, toOccurrence, wallClockDate, wallClockStamp, type ChartPiece, type ChartQuery, type ChartSnapshot } from './note-pieces';

/** The typed trigger: a backslash at the very end of the text (the catalog's `/\\$/`). */
export const READOUT_TRIGGER = /\\$/;

/** One row of the picker: a chart query with the words the catalog shows (title, and its kind and date). */
export interface ReadoutSource {
  query: ChartQuery;
  title: string;
  meta: string;
}

/** The query's title, as the catalog names it: "Vitals", or the lab set's name. */
export function readoutTitle(query: ChartQuery): string {
  return chartName(query);
}

/** The kind and date under a title: "Lab · 12 Sep 2026" (the newest collection date), "Vitals · 3 Nov 2026, 08:45 · <who>". */
export function readoutMeta(snapshot: ChartSnapshot): string {
  if (snapshot.query === 'vitals.latest') {
    const newest = newestOf(snapshot.results.map((r) => r.measuredAt));
    const by = snapshot.results.find((r) => r.recordedBy)?.recordedBy;
    return ['Vitals', wallClockStamp(newest), by].filter(Boolean).join(' · ');
  }
  const newest = newestOf(snapshot.results.map((r) => r.collectedAt));
  return ['Lab', wallClockDate(newest)].filter(Boolean).join(' · ');
}

function newestOf(times: readonly (string | undefined)[]): string | undefined {
  return times.filter((t): t is string => !!t).sort().at(-1);
}

/** The queries the picker can offer, in the catalog's order: the lab sets, then the vitals. */
export const READOUT_QUERIES: readonly ChartQuery[] = [...LAB_SETS.map((s) => `labs.set:${s.id}` as ChartQuery), 'vitals.latest'];

/** The picker's rows: each query whose read came back with values, minus the ones already in the line. */
export function readoutSources(resolve: (q: ChartQuery) => ChartSnapshot, inserted: readonly ChartQuery[] = []): ReadoutSource[] {
  const rows: ReadoutSource[] = [];
  for (const query of READOUT_QUERIES) {
    if (inserted.includes(query)) continue;
    const snapshot = resolve(query);
    if (snapshot.status !== 'ok' || snapshot.results.length === 0) continue;
    rows.push({ query, title: readoutTitle(query), meta: readoutMeta(snapshot) });
  }
  return rows;
}

/** A new readout for `query`, resolved now against the line's reads (one transaction, as a phrase's chart piece). */
export function newReadout(query: ChartQuery, resolve: (q: ChartQuery) => ChartSnapshot): ChartPiece {
  const piece = toOccurrence({ v: 1, t: 'chart', id: '', query, label: readoutTitle(query) }, resolve) as ChartPiece;
  return { ...piece, readout: true };
}

export function isReadout(seg: LineSeg): seg is LineSeg & { piece: ChartPiece } {
  return seg.piece?.t === 'chart' && seg.piece.readout === true;
}

/** The line's segments → the text the field edits, and the readouts at its end (each read after its line break). */
export function splitReadouts(segs: readonly LineSeg[]): { body: LineSeg[]; readouts: ChartPiece[] } {
  const body = [...segs];
  const readouts: ChartPiece[] = [];
  while (body.length && isReadout(body[body.length - 1])) {
    readouts.unshift((body.pop() as LineSeg & { piece: ChartPiece }).piece);
    const last = body[body.length - 1];
    if (last && !last.piece && last.text.endsWith('\n')) {
      const text = last.text.slice(0, -1);
      if (text) body[body.length - 1] = { text };
      else body.pop();
    }
  }
  return { body, readouts };
}

/** The text and the readouts → the line's segments (the inverse of `splitReadouts`). A readout's text is the line's own
 *  (`lineDisplay`, as the line codec writes and checks it), so it reads back as a piece after the save. */
export function joinReadouts(body: readonly LineSeg[], readouts: readonly ChartPiece[]): LineSeg[] {
  return [...body, ...readouts.flatMap((piece) => [{ text: '\n' }, { text: lineDisplay(piece), piece }])];
}

/** The text with the typed trigger taken off, as the catalog's pick does (`replace(/\\$/, '').trimEnd()`). */
export function withoutTrigger(text: string): string {
  return text.replace(READOUT_TRIGGER, '').trimEnd();
}
